import React, { FC, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form/dist/index.ie11';

import { FieldMessage } from '@blueprint/ui';

import {
    FieldContainer,
    FormSection,
    ReassuranceText,
    RegisterButton
} from '../AuthModal.styles';
import { RadioGroup, RadioButton } from 'shared/components';
import PreferencesFormData from './../interfaces/PreferencesFormData';
import useUpdatePreferences from '../hooks/useUpdatePreferences';
import { useUser } from 'shared/hooks/useUser';
import { trackAuthSegmentEvent } from '../utils/segmentTrack';
import { PreferencesProps } from './Preferences.types';
import useUpdateAboutMe from '../hooks/useUpdateAboutMe';
import { trackAboutMe, trackOptin, trackSubmit } from './Preferences.tracking';
import { AboutYou } from '../common/AboutYou';

const PreferencesForm: FC<PreferencesProps> = ({ close, actionAfterAuth }) => {
    const {
        register,
        handleSubmit,
        errors,
        trigger
    } = useForm<PreferencesFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });
    const user = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { updatePreferences, generalError } = useUpdatePreferences();
    const {
        updateAboutMe,
        generalError: aboutMeGeneralError
    } = useUpdateAboutMe();

    const onFormSubmit: SubmitHandler<PreferencesFormData> = async (
        formData
    ) => {
        setIsSubmitting(true);

        const payload = await updatePreferences(formData);
        const { consent__insights__24, situation } = formData;
        if (!payload?.data?.updateUserConsents) {
            return;
        }

        if (consent__insights__24 !== undefined) {
            trackOptin(consent__insights__24, actionAfterAuth?.eventLabel);

            if (consent__insights__24) {
                trackAuthSegmentEvent('optin', actionAfterAuth?.segment, user);
            }
        }

        if (situation) {
            await updateAboutMe(formData);
        }
        trackAboutMe(situation);

        trackSubmit(actionAfterAuth?.eventLabel);

        setIsSubmitting(false);
        close();
    };

    const onSubmit = handleSubmit(onFormSubmit);

    return (
        <>
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-preferences-form"
            >
                {(generalError || aboutMeGeneralError) && (
                    <FormSection>
                        <FieldMessage
                            data-testid="general-error"
                            variant="invalid"
                            message="Sorry, something went wrong. Please try again."
                        />
                    </FormSection>
                )}

                <FormSection>
                    <FieldContainer>
                        <AboutYou dropDownRef={register} />
                    </FieldContainer>
                </FormSection>

                <FormSection>
                    <RadioGroup
                        legend="Email preferences"
                        description="I want to hear about the latest property news, advice and new home developments from Zoopla and its selected partners only."
                        radioGroupName="consent__insights__24"
                        errorMessage={
                            errors.consent__insights__24 &&
                            'Please set your email preferences.'
                        }
                        onClick={() => trigger('consent__insights__24')}
                    >
                        <RadioButton
                            value="true"
                            id="yes"
                            activeTextColor="white"
                            label="Yes, please"
                            ref={register({ required: true })}
                        />
                        <RadioButton
                            value=""
                            id="no"
                            label="No, thanks"
                            activeTextColor="white"
                            activeBackgroundColor="function-error"
                            ref={register({ required: true })}
                        />
                    </RadioGroup>
                </FormSection>
                <ReassuranceText
                    size="secondary"
                    color="grey-slate"
                    isBold
                    data-testid="unsubscribe-text"
                >
                    You can unsubscribe or update your preferences at any time.
                </ReassuranceText>

                <RegisterButton
                    variant="primary"
                    isFullWidth
                    data-testid="update-preferences-button"
                    isLoading={isSubmitting}
                >
                    Save
                </RegisterButton>
            </form>
        </>
    );
};

export default PreferencesForm;
